import { useCallback, useLayoutEffect, useRef } from "react";
import type {
  JourneyDiscardPrompt,
  JourneyLeaveChoice,
} from "./quoteJourneyLeave";
export function QuoteJourneyLeaveDialog({
  labels,
  busy,
  choose,
  discardOnly = null,
}: {
  labels: string[];
  busy: boolean;
  choose: (choice: JourneyLeaveChoice) => void;
  /** 只能丢弃（如还没建草稿时已传的文件）：没有「保存并继续」，标题和丢弃按钮按它写（W6）。 */
  discardOnly?: JourneyDiscardPrompt | null;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const ignoredCloses = useRef(0);
  const closeForEffect = useCallback((node: HTMLDialogElement) => {
    if (node.open) {
      ignoredCloses.current++;
      node.close();
    }
  }, []);
  useLayoutEffect(() => {
    const node = ref.current!;
    node.showModal();
    node.querySelector<HTMLButtonElement>("[data-stay]")?.focus();
    return () => closeForEffect(node);
  }, [closeForEffect]);
  return (
    <dialog
      ref={ref}
      className="qq-leave-dialog"
      aria-labelledby="qq-leave-title"
      aria-describedby="qq-leave-description"
      onCancel={(e) => {
        e.preventDefault();
        if (!busy) choose("stay");
      }}
      onClose={() => {
        if (ignoredCloses.current > 0) {
          ignoredCloses.current--;
          return;
        }
        if (!busy) choose("stay");
      }}
    >
      <h2 id="qq-leave-title">{discardOnly?.title ?? "有修改还没保存"}</h2>
      <p id="qq-leave-description">
        {discardOnly
          ? (discardOnly.description ?? "还没建报价草稿，离开后这些不会保留：")
          : "离开前，先处理这些修改："}
      </p>
      <ul>
        {labels.map((label, index) => (
          <li key={index}>{label}</li>
        ))}
      </ul>
      <p className="qq-leave-hint">
        {discardOnly?.hint ?? "放弃只清掉未保存的输入，已经保存的内容会保留。"}
      </p>
      <div className="qq-leave-actions" aria-busy={busy}>
        {discardOnly ? (
          <button
            className="qq-leave-action qq-leave-discard"
            disabled={busy}
            onClick={() => choose("discard")}
          >
            {discardOnly.confirm}
          </button>
        ) : (
          <>
            <button
              className="qq-leave-action qq-leave-save"
              disabled={busy}
              onClick={() => choose("save")}
            >
              保存并继续
            </button>
            <button
              className="qq-leave-action"
              disabled={busy}
              onClick={() => choose("discard")}
            >
              放弃修改并继续
            </button>
          </>
        )}
        <button
          className="qq-leave-action"
          data-stay
          disabled={busy}
          onClick={() => choose("stay")}
        >
          留在这里
        </button>
      </div>
      {busy && <p role="status">正在处理，请稍等…</p>}
    </dialog>
  );
}
